import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { type DocumentTableRow } from 'features/knowledgeDocumentViewer'
import { MetadataFieldList } from 'features/knowledgeDocumentViewer/components/metadata/MetadataFieldList'
import { documentTypeLabel } from 'features/knowledgeDocumentViewer/utils/documentTypeLabels'
import { formatDrawingMetadata } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import React, { useMemo } from 'react'

interface MetadataField {
  label: MessageDescriptor
  getValue: (document: DocumentTableRow) => string | null
  formatValue?: (
    value: string,
    translate: (descriptor: MessageDescriptor) => string
  ) => string
}

const METADATA_FIELDS: readonly MetadataField[] = [
  {
    label: msg`Type`,
    getValue: (document) => document.docType,
    formatValue: documentTypeLabel,
  },
  {
    label: msg`Drawing #`,
    getValue: (document) =>
      formatDrawingMetadata(document.drawing, document.sheetNumber),
  },
  { label: msg`Vendor`, getValue: (document) => document.vendor },
  { label: msg`System`, getValue: (document) => document.system },
  {
    label: msg`Pages`,
    getValue: (document) =>
      document.numPages !== null ? String(document.numPages) : null,
  },
]

export function hasDocumentPreviewMetadata(
  document: DocumentTableRow
): boolean {
  return METADATA_FIELDS.some(({ getValue }) => Boolean(getValue(document)))
}

export const DocumentPreviewMeta: React.FC<{ document: DocumentTableRow }> = ({
  document,
}) => {
  const { i18n } = useLingui()
  const fields = useMemo(
    () =>
      METADATA_FIELDS.flatMap(({ label, getValue, formatValue }) => {
        const value = getValue(document)
        if (!value) return []
        return [
          {
            label: i18n._(label),
            value: formatValue
              ? formatValue(value, (descriptor) => i18n._(descriptor))
              : value,
          },
        ]
      }),
    [document, i18n]
  )

  return <MetadataFieldList fields={fields} />
}
